import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CalendarDays,
  Check,
  DatabaseZap,
  FileCheck2,
  GitMerge,
  Mail,
  Phone,
  PlugZap,
  Search,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "../components/ui/carousel";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import EmailGate from "../components/EmailGate";
import AIOSEmphasisSlide from "../components/slides-ai-os/AIOSEmphasisSlide";
import { getStoredContentSessionId } from "../lib/gatedContentTracking";

const slides = [
  { slug: "promise", title: "Bidirectional Integration", section: "Overview" },
  { slug: "continuous-model", title: "Continuous Integration Model", section: "Model" },
  { slug: "read-fields", title: "Read Field Breadth", section: "Proof" },
  { slug: "write-fields", title: "Write Field Breadth", section: "Proof" },
  { slug: "aios-suite", title: "10 Softwares + 20 AI Products", section: "AI OS" },
  { slug: "migration", title: "Best Experience With Asha PMS", section: "Native PMS" },
];

const pmsLogos = [
  {
    name: "Dentrix",
    src: "https://framerusercontent.com/images/FK2PxMGUliSNEptVBpWhWGjhW4.png",
  },
  {
    name: "Eaglesoft",
    src: "/assets/dental-software-logos/pms/eaglesoft.png",
  },
  {
    name: "Open Dental",
    src: "/assets/dental-software-logos/pms/opendental.png",
  },
  {
    name: "Asha PMS",
    src: "/assets/alogo.png",
  },
];

const integrationVendors = [
  {
    name: "Sikka",
    role: "Deep PMS data + writeback layer",
    strengths: ["patients", "providers", "visits", "claims", "transactions", "treatment plans", "notes", "writeback status"],
  },
  {
    name: "NexHealth",
    role: "Scheduling, patient, visit, document, and availability layer",
    strengths: ["appointments", "patients", "providers", "operatories", "procedure codes", "document uploads", "availability", "visit sync"],
  },
  {
    name: "Asha integration fabric",
    role: "Canonical workflow record across vendors",
    strengths: ["sync health", "vendor mapping", "audit trails", "human approvals", "reconciliation", "agent memory", "PMS-specific routing", "continuous upgrades"],
  },
];

const supportedPmses = ["Dentrix", "Eaglesoft", "Open Dental"];

const pmsMatrixStats = [
  { name: "Dentrix", read: "3,948", write: "306" },
  { name: "Eaglesoft", read: "3,948", write: "306" },
  { name: "Open Dental", read: "3,948", write: "306" },
];

const openDentalWriteHighlights = [
  "documents",
  "procedures",
  "perio charting",
  "payments",
  "claim payments",
  "appointments",
];

const readFieldGroups = [
  {
    title: "Patients + households",
    fields: [
      "patient_id",
      "firstname",
      "lastname",
      "birthdate",
      "chart_number",
      "email",
      "cell",
      "homephone",
      "address_line1",
      "city",
      "state",
      "zipcode",
      "guarantor_id",
      "guarantor_name",
      "subscriber_id",
      "family mapping",
    ],
  },
  {
    title: "Scheduling + capacity",
    fields: [
      "appointment_sr_no",
      "appointment_made_date",
      "date",
      "time",
      "length",
      "status",
      "provider_id",
      "provider_name",
      "operatory",
      "scheduled_code",
      "available slots",
      "practice schedule",
      "appointment status",
      "cancelled date",
      "resource_id",
      "visit amount",
    ],
  },
  {
    title: "Insurance + eligibility",
    fields: [
      "insurance_company_id",
      "insurance company name",
      "payer id",
      "plan coverage",
      "percentage_covered",
      "deductible",
      "co_pay",
      "eligibility status",
      "subscriber firstname",
      "subscriber lastname",
      "group number",
      "payor address",
      "ERA capable",
      "benefits",
      "estimated insurance",
      "payer matching",
    ],
  },
  {
    title: "Claims + RCM",
    fields: [
      "claim_sr_no",
      "claim_channel",
      "claim_sent_date",
      "claim_payment_date",
      "claim status",
      "bank_no",
      "cheque_no",
      "transaction_sr_no",
      "account balance",
      "balance_total",
      "amount_0_30",
      "amount_31_60",
      "amount_61_90",
      "amount_90_plus",
      "unapplied payments",
      "write_off_estimate",
    ],
  },
  {
    title: "Treatment + clinical",
    fields: [
      "procedure_code",
      "procedure_description",
      "treatment_plan_id",
      "treatment phase",
      "clinical_condition_id",
      "medical note",
      "note type",
      "tooth_number",
      "surface",
      "perio pocket_depth",
      "bleeding points",
      "plaque",
      "xray link",
      "prescription",
      "drug directions",
      "lab case",
    ],
  },
  {
    title: "Practice intelligence",
    fields: [
      "practice_id",
      "practice name",
      "provider mapping",
      "provider time clock",
      "fee schedule",
      "procedure categories",
      "PMS definitions",
      "general settings",
      "sync status",
      "server health",
      "data freshness",
      "practice events",
      "KPI definitions",
      "payment types",
      "referrals",
      "documents",
    ],
  },
];

const writeFieldGroups = [
  {
    title: "Appointment writes",
    fields: [
      "POST appointment",
      "PUT appointment",
      "PATCH appointment",
      "patient_id",
      "date",
      "time",
      "length",
      "provider_id",
      "operatory",
      "schedule",
      "status",
      "type",
      "description",
      "cancellation_note",
      "status_changed_by",
      "staff",
    ],
  },
  {
    title: "Patient writes",
    fields: [
      "POST patient",
      "PATCH patient",
      "firstname",
      "lastname",
      "birthdate",
      "gender",
      "address_line1",
      "address_line2",
      "city",
      "country",
      "email",
      "cell",
      "homephone",
      "other_phone",
      "language",
      "mail_method",
    ],
  },
  {
    title: "Claim + payment writes",
    fields: [
      "POST claim",
      "PATCH claims",
      "POST claim_payment",
      "POST transaction",
      "insurance_company_id",
      "transaction_sr_no",
      "claim_sr_no",
      "status",
      "date_sent",
      "date_resent",
      "internal_note",
      "custom_track_status",
      "claim_payment_date",
      "cheque_no",
      "bank_name",
      "amount",
    ],
  },
  {
    title: "Clinical documentation writes",
    fields: [
      "POST medical_note",
      "POST procedure",
      "POST perio_chart",
      "POST lab_result",
      "patient_id",
      "provider_id",
      "procedure_code",
      "procedure_date",
      "procedure_description",
      "tooth",
      "surface",
      "text",
      "description",
      "pocket_depth",
      "bleed",
      "result",
    ],
  },
  {
    title: "Documents + attachments",
    fields: [
      "POST document",
      "document_base64",
      "document_name",
      "document_type",
      "created_datetime",
      "description",
      "provider_id",
      "patient_id",
      "practice_id",
      "tooth",
      "type",
      "check_spu",
      "Request-Key",
      "Content-Type",
      "writeback id",
      "remote status",
    ],
  },
];

const cn = (...classes) => classes.filter(Boolean).join(" ");

function SlideShell({
  eyebrow,
  eyebrowIcon: EyebrowIcon,
  title,
  titleAccent,
  subtitle,
  children,
  dark = false,
  tone = "blue",
}) {
  const toneMap = {
    blue: {
      lightBadge: "bg-blue-50 text-blue-600 border-blue-100/60",
      darkBadge: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      lightAccent: "from-blue-600 to-indigo-600",
      darkAccent: "from-blue-400 to-indigo-400",
      lightOrbA: "linear-gradient(135deg, #93c5fd 0%, #c4b5fd 50%, #fbcfe8 100%)",
      lightOrbB: "linear-gradient(135deg, #a5f3fc 0%, #93c5fd 50%, #c4b5fd 100%)",
    },
    emerald: {
      lightBadge: "bg-emerald-50 text-emerald-600 border-emerald-100/60",
      darkBadge: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
      lightAccent: "from-emerald-600 to-teal-600",
      darkAccent: "from-emerald-400 to-teal-400",
      lightOrbA: "linear-gradient(135deg, #6ee7b7 0%, #93c5fd 50%, #c4b5fd 100%)",
      lightOrbB: "linear-gradient(135deg, #a7f3d0 0%, #67e8f9 50%, #93c5fd 100%)",
    },
    amber: {
      lightBadge: "bg-amber-50 text-amber-700 border-amber-100/60",
      darkBadge: "bg-amber-500/10 text-amber-400 border-amber-500/20",
      lightAccent: "from-amber-500 to-orange-600",
      darkAccent: "from-amber-300 to-orange-400",
      lightOrbA: "linear-gradient(135deg, #fde68a 0%, #fbcfe8 50%, #c4b5fd 100%)",
      lightOrbB: "linear-gradient(135deg, #fed7aa 0%, #fde68a 50%, #fbcfe8 100%)",
    },
  };
  const t = toneMap[tone] || toneMap.blue;

  return (
    <section
      className={cn(
        "relative h-full w-full overflow-hidden p-10 xl:p-12 font-sans",
        dark ? "bg-[#0B1120] text-white" : "bg-[#f8fafc] text-slate-900"
      )}
    >
      {dark ? (
        <>
          <div className="pointer-events-none absolute top-0 left-0 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600/10 blur-[120px]" />
          <div className="pointer-events-none absolute bottom-0 right-0 h-[800px] w-[800px] translate-x-1/2 translate-y-1/2 rounded-full bg-indigo-600/10 blur-[120px]" />
          <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,#ffffff08_1px,transparent_1px),linear-gradient(to_bottom,#ffffff08_1px,transparent_1px)] bg-[size:48px_48px]" />
        </>
      ) : (
        <>
          <div
            className="pointer-events-none absolute -top-[200px] -right-[120px] h-[700px] w-[700px] rounded-full opacity-30 blur-[120px]"
            style={{ background: t.lightOrbA }}
          />
          <div
            className="pointer-events-none absolute -bottom-[200px] -left-[120px] h-[600px] w-[600px] rounded-full opacity-25 blur-[120px]"
            style={{ background: t.lightOrbB }}
          />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(#cbd5e1_0.8px,transparent_0.8px)] [background-size:28px_28px] opacity-40" />
        </>
      )}

      <div className="relative z-10 flex h-full flex-col">
        <motion.header
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-7 max-w-5xl"
        >
          {eyebrow && (
            <div
              className={cn(
                "mb-3 inline-flex items-center gap-2 rounded-full border px-3.5 py-1 text-[13px] font-semibold backdrop-blur-md",
                dark ? t.darkBadge : t.lightBadge
              )}
            >
              {EyebrowIcon && <EyebrowIcon className="h-3.5 w-3.5" />}
              <span className="tracking-wide">{eyebrow}</span>
            </div>
          )}
          <h1
            className={cn(
              "max-w-5xl text-[40px] font-bold leading-[1.05] tracking-tight",
              dark ? "text-white" : "text-slate-900"
            )}
          >
            {title}
            {titleAccent && (
              <>
                {" "}
                <span
                  className={cn(
                    "bg-gradient-to-r bg-clip-text text-transparent",
                    dark ? t.darkAccent : t.lightAccent
                  )}
                >
                  {titleAccent}
                </span>
              </>
            )}
          </h1>
          {subtitle && (
            <p
              className={cn(
                "mt-3 max-w-4xl text-[17px] leading-relaxed",
                dark ? "text-slate-300" : "text-slate-600"
              )}
            >
              {subtitle}
            </p>
          )}
        </motion.header>
        <div className="min-h-0 flex-1">{children}</div>
      </div>
    </section>
  );
}

function MetricPill({ label, value, tone = "blue", dark = false }) {
  const tones = dark
    ? {
        blue: "border-blue-500/25 bg-blue-500/10 text-blue-200",
        emerald: "border-emerald-500/25 bg-emerald-500/10 text-emerald-200",
        amber: "border-amber-500/25 bg-amber-500/10 text-amber-200",
      }
    : {
        blue: "border-blue-200/70 bg-white text-blue-700",
        emerald: "border-emerald-200/70 bg-white text-emerald-700",
        amber: "border-amber-200/70 bg-white text-amber-700",
      };
  return (
    <div className={cn("rounded-xl border px-4 py-3.5 shadow-sm", tones[tone])}>
      <p
        className={cn(
          "text-[10.5px] font-bold uppercase tracking-wider",
          dark ? "text-white/60" : "text-slate-400"
        )}
      >
        {label}
      </p>
      <p
        className={cn(
          "mt-1.5 text-[26px] font-bold tracking-tight",
          dark ? "text-white" : "text-slate-900"
        )}
      >
        {value}
      </p>
    </div>
  );
}

function FieldWall({ groups, tone = "blue" }) {
  const styles = {
    blue: {
      icon: "text-blue-600 bg-blue-50",
      title: "text-slate-900",
      chip: "border-blue-100 bg-blue-50/70 text-blue-900",
    },
    emerald: {
      icon: "text-emerald-600 bg-emerald-50",
      title: "text-slate-900",
      chip: "border-emerald-100 bg-emerald-50/70 text-emerald-900",
    },
  };
  const style = styles[tone];

  return (
    <div className="grid h-full grid-cols-3 gap-4">
      {groups.map((group, i) => (
        <motion.div
          key={group.title}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 + i * 0.05, duration: 0.4 }}
          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
        >
          <div className="mb-3 flex items-center gap-2">
            <div className={cn("flex h-6 w-6 items-center justify-center rounded-md", style.icon)}>
              <Check className="h-3.5 w-3.5" />
            </div>
            <p className={cn("text-[12.5px] font-bold uppercase tracking-wider", style.title)}>
              {group.title}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {group.fields.map((field) => (
              <span
                key={`${group.title}-${field}`}
                className={cn(
                  "rounded-lg border px-2 py-1 text-[11px] font-semibold leading-none",
                  style.chip
                )}
              >
                {field}
              </span>
            ))}
          </div>
        </motion.div>
      ))}
    </div>
  );
}

function PromiseSlide() {
  return (
    <SlideShell
      dark
      eyebrow="Asha Continuous Integration"
      eyebrowIcon={PlugZap}
      title="Strong bidirectional integration with"
      titleAccent="Dentrix, Eaglesoft & Open Dental."
      subtitle="Asha combines Sikka, NexHealth, and our own canonical workflow layer so the full AI OS can operate on top of the PMS you already use — with an upgrade path to the native Asha PMS experience."
    >
      <div className="grid h-full grid-cols-[1fr_440px] gap-8">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="flex flex-col justify-center gap-7"
        >
          {/* Asha → PMS integration diagram */}
          <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-md">
            <div className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-r from-blue-500/15 via-indigo-500/15 to-violet-500/15 blur-lg" />
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 p-3 shadow-lg shadow-blue-500/30">
                <img src="/assets/alogo.png" alt="Asha" className="h-full w-full object-contain" />
              </div>
              <div className="flex flex-1 items-center gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="h-px flex-1 bg-gradient-to-r from-blue-400/40 to-indigo-400/40"
                    animate={{ opacity: [0.3, 0.9, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, delay: i * 0.18 }}
                  />
                ))}
              </div>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10 ring-1 ring-blue-400/30">
                <PlugZap className="h-6 w-6 text-blue-300" />
              </div>
              <div className="flex flex-1 items-center gap-2">
                {[0, 1, 2, 3, 4].map((i) => (
                  <motion.div
                    key={i}
                    className="h-px flex-1 bg-gradient-to-r from-emerald-400/40 to-blue-400/40"
                    animate={{ opacity: [0.3, 0.9, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, delay: 0.9 + i * 0.18 }}
                  />
                ))}
              </div>
            </div>
            <div className="mt-5 grid grid-cols-3 gap-2.5">
              {pmsLogos.slice(0, 3).map((logo, i) => (
                <motion.div
                  key={logo.name}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1, duration: 0.4 }}
                  className="flex h-20 items-center justify-center rounded-xl bg-white/95 px-4 shadow-sm ring-1 ring-white/10"
                >
                  <img src={logo.src} alt={logo.name} className="max-h-12 max-w-full object-contain" />
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <MetricPill dark label="Read fields per PMS" value="3,948" tone="blue" />
            <MetricPill dark label="Write fields per PMS" value="306" tone="emerald" />
            <MetricPill dark label="AI agents enabled" value="Complete" tone="amber" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="relative rounded-2xl border border-white/10 bg-slate-900/60 p-6 shadow-2xl backdrop-blur-md"
        >
          <div className="absolute -inset-0.5 -z-10 rounded-2xl bg-gradient-to-b from-blue-500/15 to-indigo-500/15 blur-md" />
          <div className="mb-5 inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-[12px] font-semibold text-blue-300">
            <Sparkles className="h-3.5 w-3.5" />
            <span>The customer story</span>
          </div>
          <div className="space-y-4">
            {[
              "Keep Dentrix, Eaglesoft, or Open Dental and still launch the Asha AI OS workforce.",
              "Use multiple integration vendors instead of betting the experience on one connector.",
              "Give agents scheduling, insurance, claims, ledger, patient, document, and clinical context.",
              "Move to Asha PMS when you want the same workflows to become fully native.",
            ].map((item, i) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: 12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 + i * 0.08, duration: 0.4 }}
                className="flex items-start gap-3 rounded-xl border border-white/5 bg-white/[0.03] px-4 py-3"
              >
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30">
                  <Check className="h-3 w-3 text-emerald-300" />
                </div>
                <p className="text-[15px] leading-snug text-slate-200">{item}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </SlideShell>
  );
}

function ContinuousModelSlide() {
  return (
    <SlideShell
      eyebrow="Continuous Integration Model"
      eyebrowIcon={GitMerge}
      title="Three integration systems for"
      titleAccent="the strongest possible path."
      subtitle="Sikka, NexHealth, and Asha's own integration fabric give us multiple ways to read, write, document, and reconcile work — stronger coverage, backup paths, cleaner monitoring, and continuous improvement without forcing a PMS migration."
    >
      <div className="grid h-full grid-cols-[1fr_360px] gap-5">
        <div className="grid grid-cols-3 gap-4">
          {integrationVendors.map((vendor, i) => (
            <motion.div
              key={vendor.name}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.45 }}
              className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-[10.5px] font-bold uppercase tracking-wider text-blue-600">
                    Integration vendor {i + 1}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900">{vendor.name}</p>
                </div>
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <PlugZap className="h-5 w-5" />
                </div>
              </div>
              <p className="mt-3 text-[13px] font-medium leading-snug text-slate-600">
                {vendor.role}
              </p>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {vendor.strengths.map((item) => (
                  <span
                    key={item}
                    className="rounded-lg border border-blue-100 bg-blue-50/70 px-2 py-1 text-[11px] font-semibold text-blue-900"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="relative flex flex-col justify-between rounded-2xl border border-slate-800/40 bg-[#0B1120] p-6 text-white shadow-2xl overflow-hidden"
        >
          <div className="pointer-events-none absolute -top-20 -right-20 h-60 w-60 rounded-full bg-blue-600/20 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-indigo-600/20 blur-[80px]" />
          <div className="relative">
            <div className="inline-flex items-center gap-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 px-3 py-1 text-[11.5px] font-semibold text-blue-300">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>PMS coverage</span>
            </div>
            <div className="mt-5 space-y-2.5">
              {supportedPmses.map((pms, i) => (
                <motion.div
                  key={pms}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.55 + i * 0.08, duration: 0.4 }}
                  className="flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.05] px-4 py-3"
                >
                  <span className="text-[15px] font-bold text-white">{pms}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10.5px] font-bold uppercase tracking-wider text-emerald-300">
                      Live
                    </span>
                    <BadgeCheck className="h-5 w-5 text-emerald-300" />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
          <p className="relative mt-4 text-[13px] leading-relaxed text-slate-300">
            If one system is stronger for a workflow, Asha uses that path. If one is unavailable, Asha routes through another vendor or keeps the work documented in the canonical Asha operating record.
          </p>
        </motion.div>
      </div>
    </SlideShell>
  );
}

function ReadFieldsSlide() {
  return (
    <SlideShell
      eyebrow="Read Field Breadth"
      eyebrowIcon={DatabaseZap}
      title="Asha can read over"
      titleAccent="3,000 PMS data fields"
      subtitle="The Dentrix, Eaglesoft, and Open Dental Sikka matrices each catalog thousands of read fields across patient, schedule, insurance, claims, ledger, clinical, and practice operations."
    >
      <div className="h-full">
        <FieldWall groups={readFieldGroups} tone="blue" />
      </div>
    </SlideShell>
  );
}

function WriteFieldsSlide() {
  return (
    <SlideShell
      tone="emerald"
      eyebrow="Write Field Breadth"
      eyebrowIcon={FileCheck2}
      title="Writebacks across"
      titleAccent="306 critical fields."
      subtitle="The Dentrix, Eaglesoft, and Open Dental matrices each catalog a broad write-action surface across appointments, patients, claims, payments, procedures, documents, notes, labs, and perio workflows."
    >
      <div className="grid h-full grid-cols-[280px_1fr] gap-5">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.5 }}
          className="flex flex-col gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
        >
          <div className="flex items-start justify-between">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50 text-emerald-600">
              <FileCheck2 className="h-5 w-5" />
            </div>
            <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-emerald-700">
              Per PMS
            </span>
          </div>
          <div>
            <p className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-[52px] font-bold leading-none text-transparent tracking-tight">
              306
            </p>
            <p className="mt-1.5 text-[12.5px] font-bold uppercase tracking-wider text-slate-500">
              Write fields per PMS matrix
            </p>
          </div>
          <div className="space-y-2">
            {pmsMatrixStats.map((pms) => (
              <div
                key={pms.name}
                className="flex items-center justify-between rounded-xl border border-emerald-100/60 bg-emerald-50/60 px-3.5 py-2.5"
              >
                <span className="text-[12.5px] font-semibold text-slate-800">{pms.name}</span>
                <span className="text-[13px] font-bold text-emerald-700 tabular-nums">{pms.write}</span>
              </div>
            ))}
          </div>
          <div className="mt-auto rounded-xl border border-emerald-100/60 bg-emerald-50/60 p-4">
            <p className="text-[10.5px] font-bold uppercase tracking-wider text-emerald-700">
              Open Dental highlights
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {openDentalWriteHighlights.map((item) => (
                <span
                  key={item}
                  className="rounded-lg border border-emerald-200/70 bg-white px-2 py-1 text-[11px] font-bold text-emerald-800"
                >
                  {item}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
        <FieldWall groups={writeFieldGroups} tone="emerald" />
      </div>
    </SlideShell>
  );
}

function AIOSSuiteSlide() {
  return <AIOSEmphasisSlide />;
}

function MigrationSlide() {
  return (
    <SlideShell
      eyebrow="Native PMS Upside"
      eyebrowIcon={Sparkles}
      title="Integrate now,"
      titleAccent="migrate to Asha PMS when you're ready."
      subtitle="Migration isn't required for the agents to work. It's the path to the most native version of Asha — fewer connector boundaries, richer native writebacks, and a single AI-native operating record."
    >
      <div className="grid h-full grid-cols-2 gap-5">
        {/* INTEGRATE */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="relative flex flex-col rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
        >
          <div className="mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                <PlugZap className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-blue-600">
                  Option A
                </p>
                <h2 className="text-3xl font-bold text-slate-900">Integrate</h2>
              </div>
            </div>
            <span className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-blue-700">
              Start here
            </span>
          </div>
          <div className="space-y-3">
            {[
              ["Best for", "Fast launch while keeping Dentrix, Eaglesoft, or Open Dental as the PMS of record."],
              ["Strength", "Full AI agents on the existing PMS with live operational context."],
              ["Operating model", "Asha runs workflows and documents completed work back into the PMS workflow."],
              ["What remains", "Selected last-mile updates stay connector-specific while Asha continues improving the integration."],
            ].map(([label, body]) => (
              <div key={label} className="rounded-xl border border-slate-100 bg-slate-50/70 p-4">
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-blue-600">
                  {label}
                </p>
                <p className="mt-1 text-[14px] leading-snug text-slate-700">{body}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* MIGRATE */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5 }}
          className="relative flex flex-col rounded-2xl border border-blue-200 bg-white p-6 shadow-[0_24px_60px_-30px_rgba(37,99,235,0.45)] overflow-hidden"
        >
          <div className="pointer-events-none absolute -top-20 -right-20 h-60 w-60 rounded-full bg-blue-100/70 blur-[80px]" />
          <div className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-indigo-100/70 blur-[80px]" />
          <div className="relative mb-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-md shadow-blue-500/30">
                <Sparkles className="h-6 w-6" />
              </div>
              <div>
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-indigo-600">
                  Option B
                </p>
                <h2 className="text-3xl font-bold text-slate-900">Migrate</h2>
              </div>
            </div>
            <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider text-amber-700">
              Most native
            </span>
          </div>
          <div className="relative space-y-3">
            {[
              ["Best for", "Practices ready for a native AI operating system."],
              ["Strength", "Every workflow and field is first-class inside the same product."],
              ["Advantage", "Deep writebacks for forms, charting, scheduling, payments, claims, and agent memory."],
              ["Outcome", "Fewer handoffs, cleaner data, and a better automation experience for staff."],
            ].map(([label, body]) => (
              <div key={label} className="rounded-xl border border-blue-100/70 bg-blue-50/70 p-4">
                <p className="text-[10.5px] font-bold uppercase tracking-wider text-indigo-700">
                  {label}
                </p>
                <p className="mt-1 text-[14px] leading-snug text-slate-700">{body}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </SlideShell>
  );
}

const slideComponents = [
  PromiseSlide,
  ContinuousModelSlide,
  ReadFieldsSlide,
  WriteFieldsSlide,
  AIOSSuiteSlide,
  MigrationSlide,
];

const ACCESS_STORAGE_KEY = "asha_integration_fields_deck_access";
const SESSION_STORAGE_KEY = "asha_integration_fields_deck_session_id";

function IntegrationFieldsDeckPage() {
  const [api, setApi] = useState(null);
  const [current, setCurrent] = useState(0);
  const [query, setQuery] = useState("");
  const [scale, setScale] = useState(1);
  const [isLocked, setIsLocked] = useState(true);
  const containerRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const filteredSlides = useMemo(
    () =>
      slides
        .map((slide, index) => ({ ...slide, index }))
        .filter((slide) =>
          `${slide.title} ${slide.section}`.toLowerCase().includes(query.toLowerCase())
        ),
    [query]
  );

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      setScale(containerRef.current.offsetWidth / 1400);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const hasAccess = localStorage.getItem(ACCESS_STORAGE_KEY);
    const sessionId = getStoredContentSessionId(SESSION_STORAGE_KEY);
    if (hasAccess && sessionId) {
      setIsLocked(false);
    }
  }, []);

  const handleAccessGranted = (email) => {
    localStorage.setItem(ACCESS_STORAGE_KEY, "true");
    console.log("Integration fields access granted for:", email);
    setIsLocked(false);
  };

  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => {
      const next = api.selectedScrollSnap();
      setCurrent(next);
      setSearchParams({ slide: slides[next]?.slug || slides[0].slug }, { replace: true });
    };
    api.on("select", onSelect);
    return () => api.off("select", onSelect);
  }, [api, setSearchParams]);

  useEffect(() => {
    if (!api) return;
    const slideParam = searchParams.get("slide");
    if (!slideParam) return;
    const index = slides.findIndex((slide) => slide.slug === slideParam);
    if (index >= 0) {
      setTimeout(() => api.scrollTo(index), 80);
    }
  }, [api, searchParams]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!api) return;
      if (event.key === "ArrowRight") api.scrollNext();
      if (event.key === "ArrowLeft") api.scrollPrev();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [api]);

  return (
    <div className="relative flex min-h-screen w-screen flex-col items-center overflow-x-hidden bg-neutral-950 p-3 text-white xl:h-screen xl:overflow-hidden xl:p-6">
      <AnimatePresence>
        {isLocked && (
          <EmailGate
            onAccessGranted={handleAccessGranted}
            sessionStorageKey={SESSION_STORAGE_KEY}
          />
        )}
      </AnimatePresence>

      <div className="mb-4 flex w-full max-w-[1800px] items-center justify-between gap-3">
        <div className="relative w-[280px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/35" />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search slides..."
            className="w-full rounded-md border border-white/10 bg-white/5 py-2.5 pl-9 pr-3 text-sm text-white outline-none transition placeholder:text-white/25 focus:border-blue-400/60 focus:ring-2 focus:ring-blue-500/20"
          />
        </div>

        <div className="flex items-center gap-3">
          <p className="hidden text-sm font-medium text-white/55 md:block">
            {current + 1} / {slides.length}
          </p>
          <Dialog>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                className="border-white/10 bg-white/5 text-white hover:bg-white/10 hover:text-white"
              >
                I have questions
              </Button>
            </DialogTrigger>
            <DialogContent className="overflow-hidden rounded-lg border-0 bg-white p-0 shadow-2xl sm:max-w-md">
              <DialogHeader className="p-6 pb-2">
                <DialogTitle className="text-2xl font-bold tracking-tight text-slate-950">
                  Talk through the integration
                </DialogTitle>
                <DialogDescription className="text-base text-slate-500">
                  We can review your PMS, Sikka scope, and rollout path.
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-col gap-3 p-6 pt-2">
                <Button
                  className="h-auto justify-start rounded-md bg-slate-950 px-4 py-4 text-left text-white hover:bg-blue-700"
                  onClick={() =>
                    window.open(
                      "https://calendly.com/asha-health/dental-one-stop-shop",
                      "_blank"
                    )
                  }
                >
                  <CalendarDays className="h-5 w-5" />
                  Book a meeting
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start rounded-md px-4 py-4 text-left"
                  onClick={() => (window.location.href = "sms:+19146779846")}
                >
                  <Phone className="h-5 w-5" />
                  Text (914) 677-9846
                </Button>
                <Button
                  variant="outline"
                  className="h-auto justify-start rounded-md px-4 py-4 text-left"
                  onClick={() =>
                    (window.location.href = "mailto:onboarding@asha.health")
                  }
                >
                  <Mail className="h-5 w-5" />
                  onboarding@asha.health
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="flex w-full max-w-[1800px] flex-1 flex-col items-center justify-center gap-5 xl:min-h-0 xl:flex-row">
        <aside className="order-2 flex w-full flex-none flex-col xl:order-1 xl:h-full xl:w-[270px] xl:pb-7">
          <ScrollArea className="min-h-[180px] flex-1 pr-3 xl:min-h-0">
            <div className="space-y-1 border-l border-white/10 pl-4">
              {filteredSlides.map((slide) => (
                <button
                  key={slide.slug}
                  onClick={() => api?.scrollTo(slide.index)}
                  className={cn(
                    "group relative flex w-full flex-col py-2 pl-3 text-left transition",
                    current === slide.index ? "opacity-100" : "opacity-45 hover:opacity-80"
                  )}
                >
                  <span
                    className={cn(
                      "absolute -left-[17px] top-2 h-8 w-[3px] rounded-full transition",
                      current === slide.index ? "bg-blue-400" : "bg-transparent"
                    )}
                  />
                  <span className="text-[11px] font-bold uppercase tracking-[0.13em] text-white/35">
                    {slide.section}
                  </span>
                  <span className={cn("mt-0.5 truncate text-sm font-semibold", current === slide.index ? "text-blue-300" : "text-white")}>
                    {slide.title}
                  </span>
                </button>
              ))}
            </div>
          </ScrollArea>
        </aside>

        <main className="order-1 w-full max-w-[1400px] flex-shrink-0 xl:order-2 xl:flex-1">
          <div className="aspect-video w-full">
            <div ref={containerRef} className="relative h-full w-full overflow-hidden">
              <div
                style={{
                  width: "1400px",
                  height: "787.5px",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                  position: "absolute",
                  inset: 0,
                }}
              >
                <Carousel setApi={setApi} className="h-full w-full">
                  <div className="relative h-full w-full overflow-hidden rounded-lg bg-white shadow-2xl ring-1 ring-white/10">
                    <CarouselContent className="ml-0 h-full">
                      {slideComponents.map((Slide, index) => (
                        <CarouselItem key={slides[index].slug} className="h-full pl-0">
                          <motion.div
                            className="h-full"
                            initial={{ opacity: 0.98 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                          >
                            <Slide />
                          </motion.div>
                        </CarouselItem>
                      ))}
                    </CarouselContent>
                  </div>
                </Carousel>
              </div>
            </div>
          </div>
        </main>
      </div>

      <button
        aria-label="Previous slide"
        onClick={() => api?.scrollPrev()}
        className="fixed bottom-5 left-5 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur transition hover:bg-white/20 md:flex"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>
      <button
        aria-label="Next slide"
        onClick={() => api?.scrollNext()}
        className="fixed bottom-5 right-5 z-20 hidden h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white shadow-lg backdrop-blur transition hover:bg-white/20 md:flex"
      >
        <ArrowRight className="h-5 w-5" />
      </button>
    </div>
  );
}

export default IntegrationFieldsDeckPage;
